import { ArrowDown, ArrowUp } from 'lucide-react';

import { cn } from '~/utils/cn';

export interface DeltaBadgeProps {
  /** Signed change. Drives the arrow direction and green/red tone. */
  delta: number;
  /** Optional unit appended after the number, e.g. "pts". */
  unit?: string;
  /** `data-testid` for the pill (default `delta-badge`); derives `-value`. */
  testId?: string;
}

/**
 * Small pill showing a signed delta with a directional arrow — green
 * (success) for a positive change, red (destructive) for negative. Shared
 * visual language for "before → after" stat cards across the app.
 */
export function DeltaBadge({ delta, unit, testId = 'delta-badge' }: DeltaBadgeProps) {
  const positive = delta >= 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const sign = positive ? '+' : '';

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md px-2 py-0.5',
        positive ? 'bg-success-50' : 'bg-brand-50',
      )}
      data-testid={testId}
    >
      <Icon
        className={cn('h-3 w-3 shrink-0', positive ? 'text-success' : 'text-destructive')}
        aria-hidden="true"
      />
      <span
        className={cn('text-xs font-semibold', positive ? 'text-success' : 'text-destructive')}
        data-testid={`${testId}-value`}
      >
        {sign}
        {delta}
        {unit ? ` ${unit}` : ''}
      </span>
    </div>
  );
}
